import { useRef, useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Debug logger for background polling.
 * Logs are only emitted when debug mode is active.
 *
 * Activate in the browser console:
 *   localStorage.setItem('POLLING_DEBUG', 'true')
 *
 * Deactivate:
 *   localStorage.removeItem('POLLING_DEBUG')
 */
const isDebugEnabled = () =>
  typeof window !== 'undefined' &&
  localStorage.getItem('POLLING_DEBUG') === 'true';

const debugLog = (...args) => {
  if (isDebugEnabled()) console.log('[Polling Debug]', ...args);
};

const debugError = (...args) => {
  if (isDebugEnabled()) console.error('[Polling Error]', ...args);
};

const ENTITY_CONFIGS = {
  videos: {
    basePath: '/videos',
    editPath: '/videos/',
    logPrefix: '📺',
    hashFields: ['id', 'status', 'version'],
    versionField: 'version',
  },
  collections: {
    basePath: '/collections',
    editPath: '/collections/',
    logPrefix: '📚',
    hashFields: ['id', 'status', 'updatedAt'],
    versionField: 'updatedAt',
    altBasePaths: ['/colecoes'],
    altEditPaths: ['/colecoes/'],
  },
  series: {
    basePath: '/series',
    editPath: '/series/',
    logPrefix: 'Series',
    hashFields: ['id', 'status', 'updatedAt'],
    versionField: 'updatedAt',
  },
};

/**
 * Custom hook for background polling
 *
 * @param {Object} config - Configuration object
 * @param {string} config.entityType - Type of entity ('videos' | 'series' | 'collections')
 * @param {Function} config.getDataTableSilent - Function to fetch data silently
 * @param {Function} config.getDataTable - Function to update table data
 * @param {string} config.listKey - Key for data in response
 * @param {Object} config.currentFilters - Current filter state
 * @param {number} config.currentPage - Current page number
 * @param {Array} config.currentData - Current table data
 * @param {React.MutableRefObject<boolean>} config.isUpdatingRef - Shared ref for external updating flag
 */
export const useBackgroundPolling = ({
  entityType = 'videos',
  getDataTableSilent,
  getDataTable,
  listKey,
  currentFilters = {},
  currentPage = 0,
  currentData = [],
  isUpdatingRef: externalIsUpdatingRef,
}) => {
  const location = useLocation();
  const config = ENTITY_CONFIGS[entityType];

  if (!config) {
    throw new Error(`Unsupported entity type: ${entityType}`);
  }

  // Helper function to check if current path is valid for polling
  const isValidPollingPath = useCallback(() => {
    const listingPaths = [config.basePath, ...(config.altBasePaths || [])];
    const editPaths = [config.editPath, ...(config.altEditPaths || [])];
    const isOnListing = listingPaths.some(p => location.pathname.includes(p));
    const isOnEdit = editPaths.some(p => location.pathname.includes(p));
    return isOnListing && !isOnEdit;
  }, [location.pathname, config]);

  const pollingTimeoutRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const [isPolling, setIsPolling] = useState(false);
  const [lastDataHash, setLastDataHash] = useState(null);

  // Ref always mirrors isPolling so callbacks don't need it in deps
  const isPollingRef = useRef(false);
  isPollingRef.current = isPolling;

  // Helper function to check if polling can start
  const canStartPolling = useCallback(() => {
    return !document.hidden && !isPollingRef.current && isValidPollingPath();
  }, [isValidPollingPath]);
  const lastDataHashRef = useRef(null);
  const updateDebounceRef = useRef(null);
  const internalIsUpdatingRef = useRef(false);
  const isUpdatingRef = externalIsUpdatingRef || internalIsUpdatingRef;
  const lastStableDataRef = useRef(null);
  const lastUpdateTimeRef = useRef(0);
  const consecutiveChangesRef = useRef(0);
  const pollingInstanceIdRef = useRef(null);

  // Generate version info for videos
  const generateVersionInfo = useCallback(item => {
    const versions = item.versions || [];
    if (versions.length === 0) return '0-0-0';

    const latestVersion = Math.max(...versions.map(v => v.version));
    const publishedCount = versions.filter(
      v => v.status === 'PUBLISHED'
    ).length;
    const draftCount = versions.filter(v => v.status === 'DRAFT').length;
    return `${latestVersion}-${publishedCount}-${draftCount}`;
  }, []);

  const generateDataHash = useCallback(
    tableData => {
      if (!tableData?.length) {
        debugLog(`${config.logPrefix} GenerateDataHash: No data to hash`);
        return null;
      }

      const detailedHash = tableData
        .map(item => {
          const hashParts = config.hashFields.map(field => {
            if (field === 'version' && entityType === 'videos') {
              return generateVersionInfo(item);
            }
            return item[field] || '';
          });
          return `${item.id}-${hashParts.join('-')}`;
        })
        .sort()
        .join('|');

      debugLog(
        `${config.logPrefix} Hash generated for ${tableData.length} items`
      );
      return detailedHash;
    },
    [config, entityType, generateVersionInfo]
  );

  const isDataStable = useCallback(
    (newData, newHash) => {
      const now = Date.now();
      const timeSinceLastUpdate = now - lastUpdateTimeRef.current;

      if (lastStableDataRef.current === newHash) {
        consecutiveChangesRef.current = 0;
        return true;
      }

      if (consecutiveChangesRef.current > 2 && timeSinceLastUpdate < 5000) {
        debugLog(`${config.logPrefix} Data seems unstable, waiting...`, {
          consecutiveChanges: consecutiveChangesRef.current,
          timeSinceLastUpdate,
        });
        const newFirstItem = newData[0];
        const currentFirstItem = currentData[0];

        if (
          currentFirstItem &&
          currentFirstItem.id === newFirstItem.id
        ) {
          const currentVersion = currentFirstItem.version || 0;
          const newVersion = newFirstItem.version || 0;

          if (newVersion < currentVersion) {
            debugLog(
              `${config.logPrefix} Detected version regression, skipping`,
              {
                currentVersion,
                newVersion,
                itemId: currentFirstItem.id,
              }
            );
            return false;
          }
        }
      }

      return true;
    },
    [config, entityType, currentData]
  );

  // Schedule a debounced table update after detecting stable data changes
  const scheduleDebouncedUpdate = useCallback(
    newDataHash => {
      if (isUpdatingRef.current) {
        debugLog(`${config.logPrefix} Update already in progress, skipping...`);
        return;
      }

      isUpdatingRef.current = true;

      if (updateDebounceRef.current) {
        clearTimeout(updateDebounceRef.current);
      }

      updateDebounceRef.current = setTimeout(async () => {
        debugLog(`${config.logPrefix} Executing stable data update...`);
        try {
          await getDataTable({ page: currentPage, ...currentFilters });
          debugLog(`${config.logPrefix} Table update completed`);

          lastDataHashRef.current = newDataHash;
          setLastDataHash(newDataHash);
          lastStableDataRef.current = newDataHash;
          lastUpdateTimeRef.current = Date.now();
          consecutiveChangesRef.current = 0;
        } catch (error) {
          debugError(`${config.logPrefix} Error updating table:`, error);
        } finally {
          isUpdatingRef.current = false;
        }
      }, 500);
    },
    [config, getDataTable, currentPage, currentFilters]
  );

  // Handle the case where polling detects data changes
  const handleDataChanged = useCallback(
    (newData, newDataHash, previousHash) => {
      consecutiveChangesRef.current++;

      if (!isDataStable(newData, newDataHash)) {
        debugLog(`${config.logPrefix} Data not stable, skipping update`);
        return;
      }

      debugLog(
        `${config.logPrefix} Detected stable data changes, scheduling update...`,
        {
          previousItems: previousHash ? previousHash.split('|').length : 0,
          newItems: newDataHash ? newDataHash.split('|').length : 0,
          consecutiveChanges: consecutiveChangesRef.current,
        }
      );

      scheduleDebouncedUpdate(newDataHash);
    },
    [config, isDataStable, scheduleDebouncedUpdate]
  );

  // Handle the case where no data changes were detected
  const handleNoChange = useCallback(() => {
    if (consecutiveChangesRef.current > 0) {
      consecutiveChangesRef.current = 0;
      debugLog(`${config.logPrefix} Reset consecutive changes counter`);
    }
    debugLog(`${config.logPrefix} No changes detected in background polling`);
  }, [config]);

  // Core polling check function
  const checkForDataChanges = useCallback(async () => {
    if (document.hidden) {
      debugLog(`${config.logPrefix} Page not visible, skipping polling check`);
      return;
    }

    if (isUpdatingRef.current) {
      debugLog(
        `${config.logPrefix} Update in progress, skipping polling check`
      );
      return;
    }

    if (!pollingInstanceIdRef.current) {
      debugLog(
        `${config.logPrefix} No active polling instance, skipping check`
      );
      return;
    }

    debugLog(`${config.logPrefix} Starting background polling check...`, {
      entityType,
      currentPage,
      hasFilters: !!currentFilters.filter,
      timestamp: new Date().toISOString(),
      consecutiveChanges: consecutiveChangesRef.current,
    });

    try {
      const response = await getDataTableSilent({
        page: currentPage,
        ...currentFilters,
      });

      if (!response || !response[listKey]) {
        debugLog(`${config.logPrefix} No valid response from silent fetch`);
        return;
      }

      const newData = response[listKey];
      const newDataHash = generateDataHash(newData);
      const previousHash = lastDataHashRef.current;

      const hasChanged =
        lastDataHashRef.current && newDataHash !== lastDataHashRef.current;
      debugLog(`${config.logPrefix} Hash comparison:`, {
        hasChanged,
        dataCount: newData.length,
        consecutive: consecutiveChangesRef.current,
      });

      if (hasChanged) {
        handleDataChanged(newData, newDataHash, previousHash);
      } else {
        handleNoChange();
      }
    } catch (error) {
      debugError(`${config.logPrefix} Background polling error:`, error);
    }
  }, [
    config,
    entityType,
    currentPage,
    currentFilters,
    getDataTableSilent,
    generateDataHash,
    listKey,
    handleDataChanged,
    handleNoChange,
  ]);

  // Always-updated ref so timers never capture a stale checkForDataChanges
  const checkForDataChangesRef = useRef(checkForDataChanges);
  useEffect(() => {
    checkForDataChangesRef.current = checkForDataChanges;
  });

  const startBackgroundPolling = useCallback(() => {
    if (!canStartPolling()) {
      debugLog(`${config.logPrefix} Cannot start polling:`, {
        hidden: document.hidden,
        alreadyPolling: isPollingRef.current,
        validPath: isValidPollingPath(),
        currentPath: location.pathname,
      });
      return;
    }

    const instanceId = Date.now() + Math.random();
    pollingInstanceIdRef.current = instanceId;
    debugLog(`${config.logPrefix} Starting background polling...`, {
      entityType,
      instanceId,
    });
    setIsPolling(true);

    const intervals = [15000, 30000, 45000, 60000];
    let currentIndex = 0;
    let regularPollingCount = 0;
    const maxRegularPollingAttempts = 2;

    const scheduleNext = () => {
      if (currentIndex < intervals.length) {
        const nextInterval = intervals[currentIndex];
        debugLog(
          `${config.logPrefix} Scheduling next polling check in ${nextInterval}ms (${currentIndex + 1}/${intervals.length})`
        );

        pollingTimeoutRef.current = setTimeout(async () => {
          debugLog(
            `${config.logPrefix} Executing polling check ${currentIndex + 1}/${intervals.length}`
          );
          await checkForDataChangesRef.current();
          currentIndex++;
          scheduleNext();
        }, nextInterval);
      } else {

        debugLog(
          `${config.logPrefix} Switching to regular 60s interval polling`
        );
        pollingIntervalRef.current = setInterval(() => {
          regularPollingCount++;
          debugLog(
            `${config.logPrefix} Regular interval polling check ${regularPollingCount}/${maxRegularPollingAttempts}`
          );

          if (regularPollingCount >= maxRegularPollingAttempts) {
            debugLog(
              `${config.logPrefix} Reached maximum regular polling attempts (${maxRegularPollingAttempts}), stopping background polling`
            );
            stopBackgroundPollingRef.current();
            return;
          }

          checkForDataChangesRef.current();
        }, 60000);
      }
    };

    scheduleNext();
  }, [canStartPolling, config, entityType]);

  // Always-updated ref so timers never capture a stale stopBackgroundPolling
  const stopBackgroundPollingRef = useRef(null);

  const stopBackgroundPolling = useCallback(() => {
    // Guard using ONLY refs — avoids stale closure on isPolling
    if (
      !pollingInstanceIdRef.current &&
      !pollingTimeoutRef.current &&
      !pollingIntervalRef.current
    ) {
      return;
    }

    debugLog(`${config.logPrefix} Stopping background polling...`, {
      entityType,
      instanceId: pollingInstanceIdRef.current,
    });
    setIsPolling(false);
    pollingInstanceIdRef.current = null;

    // Clear all timers
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (updateDebounceRef.current) {
      clearTimeout(updateDebounceRef.current);
      updateDebounceRef.current = null;
    }

    // Reset internal polling state (isUpdatingRef is owned by consumer)
    consecutiveChangesRef.current = 0;
    lastUpdateTimeRef.current = 0;
    lastDataHashRef.current = null;
    setLastDataHash(null);
    lastStableDataRef.current = null;

    debugLog(`${config.logPrefix} Cleared polling state and data cache`);
  }, [config, entityType]);

  // Keep stopBackgroundPollingRef always up-to-date
  useEffect(() => {
    stopBackgroundPollingRef.current = stopBackgroundPolling;
  });

  const updateDataHash = useCallback(
    data => {
      if (data && Array.isArray(data)) {
        const newHash = generateDataHash(data);
        lastDataHashRef.current = newHash;
        setLastDataHash(newHash);
        lastStableDataRef.current = newHash;
        lastUpdateTimeRef.current = Date.now();
        consecutiveChangesRef.current = 0;

        debugLog(`${config.logPrefix} Initial hash set for polling comparison`);
      }
    },
    [generateDataHash, config]
  );

  return {
    isPolling,

    startBackgroundPolling,
    stopBackgroundPolling,
    updateDataHash,

    pollingState: {
      instanceId: pollingInstanceIdRef.current,
      lastHash: lastDataHash,
      consecutiveChanges: consecutiveChangesRef.current,
    },
  };
};

export default useBackgroundPolling;
